import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { Subject, take, takeUntil } from 'rxjs';
import { CreditCardTypeService } from 'src/app/services/creditcardtype';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { Router } from '@angular/router';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { EActionTypes, EToaster, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { GeneralService } from 'src/app/services/general.service';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';
import { ASLicenseSettings } from 'src/app/Shared/model/GeneralModel';

@Component({
  selector: 'app-ops-credit-debit-cards',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, UserPermissionHandlerDirective, TooltipModule,
    PercentageFormatterDirective, CurrencyFormatterDirective],
  templateUrl: './ops-credit-debit-cards.html',
  styleUrl: './ops-credit-debit-cards.scss',
  providers: [FilterService],
})

export class OpsCreditDebitCards implements OnInit, OnDestroy {

  @ViewChild('gridCreditDebit', { static: false }) public gridCreditDebit: GridComponent;
  private destroy$ = new Subject<void>();

  isCollapsed = true;
  creditCardTypes: any[] = [];
  creditCardFeeSettings: any;
  creditCardFeeSettingsForm!: FormGroup;
  creditCardTypeForm!: FormGroup;
  originalFeeSettings: any;
  originalCreditCardTypeValues: any = {};
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CreditDebitCard, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CreditDebitCard, Action: EActionTypes.CREATE };
  deletePermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CreditDebitCard, Action: EActionTypes.DELETE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CreditDebitCard, Action: EActionTypes.EDIT };
  enableOnlinePayment: boolean = false;

  constructor(
    private creditCardTypeService: CreditCardTypeService,
    private generalService: GeneralService,
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.getASCloudLicenseSettings();
    this.getCreditCardTypes();
    this.getCreditCardFeeSettings();
  }

  get editRight(): boolean {
    return this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.CreditDebitCard).EditRight;
  }

  initForms(): void {
    this.creditCardFeeSettingsForm = this.fb.group({
      ChargeCCProcessingFee: [false],
      ChargeSalesTaxOnCCProcessingFee: [false],
      ExcludeCCProcessingFeeForRental: [false],
      ExcludeCCProcessingFeeForPOS: [false],
      ApplyOnlineRentalCCFee: [false],
      OnlineRentalCCFee: [0],
      OnlineRentalCCFeeType: [''],
    });

    this.creditCardTypeForm = this.fb.group({
      CreditCardTypeId: [null],
      CompanyCreditCardTypeDesc: ['', [Validators.required, Validators.maxLength(30)]],
      ChargeType: ['', [Validators.required, Validators.maxLength(30)]],
      Charge: ['', Validators.required],
      CardType: ['', [Validators.required, Validators.maxLength(20)]],
      DeleteFlag: [false],
      IsInactive: [false]
    });

    this.creditCardFeeSettingsForm.get('ChargeCCProcessingFee')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.updateExcludeProcessingFeeControls(enabled);
      });
  }

  getCreditCardTypes(): void {
    this.creditCardTypeService.GetAllCreditCardsByMarket()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.creditCardTypes = res || [];
        },
        error: (err) => {
          console.error('Failed to load credit card types', err);
        }
      });
  }

  getCreditCardFeeSettings(): void {
    this.creditCardTypeService.getCreditCardFeeSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.creditCardFeeSettings = res;
          this.creditCardFeeSettingsForm.patchValue({
            ChargeCCProcessingFee: res.ChargeCCProcessingFee ?? false,
            ChargeSalesTaxOnCCProcessingFee: res.ChargeSalesTaxOnCCProcessingFee ?? false,
            ExcludeCCProcessingFeeForRental: res.ExcludeCCProcessingFeeForRental ?? false,
            ExcludeCCProcessingFeeForPOS: res.ExcludeCCProcessingFeeForPOS ?? false,
            ApplyOnlineRentalCCFee: res.ApplyOnlineRentalCCFee ?? false,
            OnlineRentalCCFee: res.OnlineRentalCCFee ?? 0,
            OnlineRentalCCFeeType: res.OnlineRentalCCFeeType,
          });

          this.onApplyOnlineRentalCCFeeChange();
          setTimeout(() => {
            const feeType = this.creditCardFeeSettingsForm.get('OnlineRentalCCFeeType')?.value;
            const feeInput = document.querySelector<HTMLInputElement>(
              '#ccProcessingFeeFixed, #ccProcessingFeePercentage'
            );

            const control = this.creditCardFeeSettingsForm.get('OnlineRentalCCFee');
            if (!feeInput) return;
            const value = Number(control?.value ?? 0);
            if (!this.creditCardFeeSettingsForm.get('ApplyOnlineRentalCCFee')?.value ||
              control?.disabled ||
              !feeType) {
              feeInput.value = feeType === 'Percentage' ? '0.00%' : '0.00';
              return;
            }
            if (feeType === 'Fixed') {
              feeInput.value = `$${value.toFixed(2)}`;
            } else if (feeType === 'Percentage') {
              feeInput.value = `${value.toFixed(2)}%`;
            }
          });
          this.updateExcludeProcessingFeeControls(
            this.creditCardFeeSettingsForm.get('ChargeCCProcessingFee')?.value ?? false
          );

          this.originalFeeSettings = this.creditCardFeeSettingsForm.getRawValue();
          this.creditCardFeeSettingsForm.markAsPristine();
        },
        error: (err) => {
          console.error('Failed to load credit card fee settings', err);
        }
      });
  }

  validateCreditCardTypeForm(): boolean {
    if (this.creditCardTypeForm.invalid) {
      this.creditCardTypeForm.markAllAsTouched();
      const missingFields: string[] = [];
      if (this.creditCardTypeForm.get('CompanyCreditCardTypeDesc')?.hasError('required')) {
        missingFields.push('Type');
      }
      if (this.creditCardTypeForm.get('ChargeType')?.hasError('required')) {
        missingFields.push('Charge Type');
      }
      if (this.creditCardTypeForm.get('Charge')?.hasError('required')) {
        missingFields.push('Amount');
      }
      if (this.creditCardTypeForm.get('CardType')?.hasError('required')) {
        missingFields.push('Card Type');
      }
      if (missingFields.length > 0) {
        this.toastr.warning(
          `Please fill: ${missingFields.join(', ')}`,
          'Validation',
          this.sharedHTMLSettings.ToasterConfig
        );
      }
      return false;
    }
    return true
  }

  saveCreditCardType(): void {
    if (!this.validateCreditCardTypeForm()) {
      return;
    }

    const formValue = this.creditCardTypeForm.value;
    const enteredName = formValue.CompanyCreditCardTypeDesc.trim().toLowerCase();

    if (formValue.CreditCardTypeId && !this.hasCreditCardTypeChanges()) {
      this.toastr.info(
        $localize`No changes.`,
        $localize`Info`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const isDuplicate = this.creditCardTypes.some(
      p =>
        p.CompanyCreditCardTypeDesc.trim().toLowerCase() === enteredName &&
        p.CreditCardTypeId !== formValue.CreditCardTypeId
    );

    if (isDuplicate) {
      this.toastr.warning(
        $localize`Credit/Debit Card Type with name "${formValue.CompanyCreditCardTypeDesc}" already exists.`,
        $localize`Duplicate`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const payload: any = {
      CreditCardTypeId: formValue.CreditCardTypeId || null,
      CompanyCreditCardTypeDesc: formValue.CompanyCreditCardTypeDesc?.trim(),
      ChargeType: formValue.ChargeType,
      Charge: parseFloat(formValue.Charge),
      CardType: formValue.CardType?.trim(),
      DeleteFlag: formValue.DeleteFlag ?? false,
      IsInactive: formValue.IsInactive ?? false
    };

    const isUpdating = !!payload.CreditCardTypeId;

    Swal.fire({
      title: $localize`Confirm Save`,
      text: isUpdating
        ? $localize`Do you want to update this Credit/Debit Card ?`
        : $localize`Do you want to add this Credit/Debit Card ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isUpdating ? $localize`Yes, Update` : $localize`Yes, Add`,
      cancelButtonText: $localize`No`
    }).then(result => {
      if (result.isConfirmed) {
        this.creditCardTypeService.saveCreditCardTypesByMarket(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response?.Success) {
                const successMsg =
                  (isUpdating
                    ? $localize`Credit/Debit Card updated successfully`
                    : $localize`Credit/Debit Card added successfully`);
                this.toastr.success(successMsg, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                this.getCreditCardTypes();
                this.resetCreditCardTypeForm();
                this.closeCreditCardTypeModal();
              } else {
                this.toastr.error(
                  response?.Message || $localize`Failed to save Credit/Debit Card Type`,
                  $localize`Error`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
            },
            error: (err) => {
              this.toastr.error(
                $localize`Failed to save Credit/Debit Card Type.`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
              console.error('Failed to save card type', err);
            }
          });
      }
    });
  }

  deleteCreditCardType(row: any): void {
    if (!row || !row.CreditCardTypeId) {
      this.toastr.error('Invalid record', 'Error', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`This Credit/Debit Card Type will be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, delete it!`,
      cancelButtonText: $localize`Cancel`
    }).then(result => {
      if (result.isConfirmed) {
        const payload = {
          ...row,
          DeleteFlag: true
        };

        this.creditCardTypeService.saveCreditCardTypesByMarket(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response: any) => {
              const message = response?.Message || "Credit card type deleted successfully.";
              const isSuccess = response?.Success ?? true;

              if (isSuccess) {
                this.toastr.success(message, 'Success', this.sharedHTMLSettings.ToasterConfig);
                this.getCreditCardTypes();
              } else {
                this.toastr.error(message, 'Warning', this.sharedHTMLSettings.ToasterConfig);
              }

            },
            error: () => {
              this.toastr.error(
                $localize`Error deleting Credit/Debit Card Type`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
            }
          });
      }
    });
  }

  saveCreditCardFeeSettings(): void {
    if (!this.hasCreditCardFeeFormChanges()) {
      // this.toastr.info(
      //   $localize`There are no changes to save.`,
      //   $localize`Info`,
      //   this.sharedHTMLSettings.ToasterConfig
      // );
      return;
    }


    const form = this.creditCardFeeSettingsForm;
    const applyFee = form.get('ApplyOnlineRentalCCFee')?.value;
    const feeType = form.get('OnlineRentalCCFeeType')?.value;
    const feeValue = Number(form.get('OnlineRentalCCFee')?.value ?? 0);

    if (applyFee) {
      if (!feeType) {
        this.toastr.warning('Please select the CC Fee Type.', 'Info', this.sharedHTMLSettings.ToasterConfig);
        return;
      }

      if (!Number.isFinite(feeValue) || feeValue <= 0) {
        this.toastr.warning('CC Processing Fee cannot be zero or empty.', 'Info', this.sharedHTMLSettings.ToasterConfig);
        return;
      }
    }

    Swal.fire({
      title: $localize`Confirm Save`,
      text: $localize`Do you want to save the changes?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Save`,
      cancelButtonText: $localize`No`
    }).then(result => {
      if (result.isConfirmed) {
        const payload = this.creditCardFeeSettingsForm.getRawValue();

        this.creditCardTypeService.saveCreditCardFeeSettings(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.toastr.success(
                $localize`Credit/Debit Card Fee Settings saved successfully`,
                $localize`Success`,
                this.sharedHTMLSettings.ToasterConfig
              );

              this.originalFeeSettings = { ...this.creditCardFeeSettingsForm.getRawValue() };
              this.creditCardFeeSettingsForm.markAsPristine();
            },
            error: (err) => {
              Swal.fire({
                title: $localize`Error`,
                text: $localize`Failed to save settings.`,
                icon: 'error',
                confirmButtonText: $localize`OK`
              });
              console.error('Failed to save settings', err);
            }
          });
      }
    });
  }

  resetCreditCardTypeForm(): void {
    this.creditCardTypeForm.reset({
      CreditCardTypeId: null,
      CompanyCreditCardTypeDesc: '',
      ChargeType: '',
      Charge: '',
      CardType: '',
      DeleteFlag: false,
      IsInactive: false
    });
  }

  onCancel(): void {
    const isChanged = this.hasCreditCardFeeFormChanges()
    if (this.editRight && isChanged) {
      Swal.fire({
        title: $localize`Confirm`,
        text: $localize`Unsaved changes will be lost. Are you sure you want to cancel?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`
      }).then(result => {
        if (result.isConfirmed) {
          this.creditCardFeeSettingsForm.reset(this.originalFeeSettings);
          this.creditCardFeeSettingsForm.markAsPristine();
          this.isCollapsed = true;
          this.router.navigate(['/dashboard']);
        }
      });
    } else {
      this.isCollapsed = true;
      this.router.navigate(['/dashboard']);
    }
  }

  onRowSelected(args: any): void {
    const rowData = args.data;
    if (!rowData) return;

    if (!this.editRight) {
      this.toastr.info(
        EToaster.MsgInfoNoRightToEdit + $localize`:@@Credit/Debit Card:Credit/Debit Card`,
        EToaster.TitleInfo,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    this.creditCardTypeForm.patchValue({
      CreditCardTypeId: rowData.CreditCardTypeId,
      CompanyCreditCardTypeDesc: rowData.CompanyCreditCardTypeDesc,
      ChargeType: rowData.ChargeType,
      Charge: this.formatNumber(rowData.Charge),
      CardType: rowData.CardType,
      DeleteFlag: rowData.DeleteFlag ?? false,
      IsInactive: rowData.IsInactive ?? false
    });

    this.originalCreditCardTypeValues = {
      CompanyCreditCardTypeDesc: rowData.CompanyCreditCardTypeDesc,
      ChargeType: rowData.ChargeType,
      Charge: rowData.Charge,
      CardType: rowData.CardType
    };

    this.openCreditCardTypeModal()
  }

  openCreditCardTypeModal() {
    const modalEl = document.getElementById('creditCardTypeModal');
    if (modalEl) {
      let modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modalInstance) {
        modalInstance = new window['bootstrap'].Modal(modalEl, {
          backdrop: 'static',
          keyboard: false
        });
      }
      modalInstance.show();
    }
  }

  closeCreditCardTypeModal() {
    const modalEl = document.getElementById('creditCardTypeModal');
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  allowOnlyDigits(event: KeyboardEvent, maxBeforeDecimal = 6, maxAfterDecimal = 4) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];
    if (allowedKeys.includes(event.key)) return;
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cursorPos = input.selectionStart ?? value.length;
    if (!/^\d$/.test(event.key) && event.key !== '.') {
      event.preventDefault();
      return;
    }
    if (event.key === '.' && value.includes('.')) {
      event.preventDefault();
      return;
    }
    const [before = '', after = ''] = value.split('.');
    if (event.key !== '.' && cursorPos <= before.length && before.length >= maxBeforeDecimal) {
      event.preventDefault();
      return;
    }
    if (value.includes('.') && cursorPos > before.length && after.length >= maxAfterDecimal) {
      event.preventDefault();
    }
  }

  hasCreditCardTypeChanges(): boolean {
    if (!this.originalCreditCardTypeValues) return true;

    const currentValue = this.creditCardTypeForm.getRawValue();

    return (
      (currentValue.CompanyCreditCardTypeDesc?.trim() !== this.originalCreditCardTypeValues.CompanyCreditCardTypeDesc?.trim()) ||
      (currentValue.ChargeType?.trim() !== this.originalCreditCardTypeValues.ChargeType?.trim()) ||
      (currentValue.Charge !== this.originalCreditCardTypeValues.Charge) ||
      (currentValue.CardType?.trim() !== this.originalCreditCardTypeValues.CardType?.trim())
    );
  }

  hasCreditCardFeeFormChanges(): boolean {
    const currentValue = this.creditCardFeeSettingsForm.getRawValue();
    return (
      currentValue.ChargeCCProcessingFee !== this.originalFeeSettings.ChargeCCProcessingFee ||
      currentValue.ChargeSalesTaxOnCCProcessingFee !== this.originalFeeSettings.ChargeSalesTaxOnCCProcessingFee ||
      currentValue.ExcludeCCProcessingFeeForRental !== this.originalFeeSettings.ExcludeCCProcessingFeeForRental ||
      currentValue.ExcludeCCProcessingFeeForPOS !== this.originalFeeSettings.ExcludeCCProcessingFeeForPOS ||
      currentValue.ApplyOnlineRentalCCFee !== this.originalFeeSettings.ApplyOnlineRentalCCFee ||
      currentValue.OnlineRentalCCFee !== this.originalFeeSettings.OnlineRentalCCFee ||
      currentValue.OnlineRentalCCFeeType !== this.originalFeeSettings.OnlineRentalCCFeeType
    );
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }


  onChargeFocus() {
    const control = this.creditCardTypeForm.get('Charge');
    if (!control) return;

    let value = control.value;

    if (value === null || value === undefined || value === '') {
      control.setValue('', { emitEvent: false });
      return;
    }
    value = parseFloat(value).toString();

    control.setValue(value, { emitEvent: false });
  }

  onChargeBlur() {
    const control = this.creditCardTypeForm.get('Charge');
    if (!control) return;

    let value = control.value;

    if (value === null || value === '' || isNaN(value)) {
      control.setValue('0.00', { emitEvent: false });
      return;
    }

    let num = parseFloat(value);
    const integerPart = Math.floor(Math.abs(num)).toString();

    if (integerPart.length > 7) {
      num = parseFloat(integerPart.slice(0, 7));
    }

    const formatted = num.toFixed(2);
    control.setValue(formatted, { emitEvent: false });
  }

  formatNumber(value: any, decimals: number = 2): string {
    if (value === null || value === undefined || value === '') {
      return (0).toFixed(decimals);
    }

    const num = parseFloat(value);

    if (isNaN(num)) {
      return (0).toFixed(decimals);
    }

    return num.toFixed(decimals);
  }

  chargeTypeAccessor = (field: string, data: any, column: any) => {
    const value = data[field];
    if (value === 'Percentage') {
      return 'Percentage(%)';
    }
    if (value === 'Fixed') {
      return 'Fixed($)';
    }
    return value;
  };

  getASCloudLicenseSettings(): void {
    const licenseSettings = this.generalService.ASLicenseSettings;
    if (licenseSettings !== null) {
      this.enableOnlinePayment = licenseSettings.EnableOnlinePayment ?? false;
      return;
    }

    this.generalService.getASCloudLicenseSettings().pipe(take(1)).subscribe({
      next: (res: ASLicenseSettings) => {
        this.generalService.ASLicenseSettings = res;
        this.enableOnlinePayment = res.EnableOnlinePayment ?? false;
      },
      error: () => {
        this.enableOnlinePayment = false;
      }
    });
  }

  onApplyOnlineRentalCCFeeChange(): void {
    const applyFee = this.creditCardFeeSettingsForm.get('ApplyOnlineRentalCCFee')?.value;
    const feeTypeControl = this.creditCardFeeSettingsForm.get('OnlineRentalCCFeeType');
    const feeControl = this.creditCardFeeSettingsForm.get('OnlineRentalCCFee');

    if (!applyFee) {
      // Reset values and disable both controls
      feeTypeControl?.setValue('', { emitEvent: false });
      feeControl?.setValue(0.00, { emitEvent: false });

      feeTypeControl?.disable({ emitEvent: false });
      feeControl?.disable({ emitEvent: false });
    } else {
      // Enable both controls
      feeTypeControl?.enable({ emitEvent: false });
      feeControl?.enable({ emitEvent: false });
    }

    this.creditCardFeeSettingsForm.markAsDirty();
  }

  onOnlineRentalCCFeeTypeChange(): void {
    const feeTypeControl = this.creditCardFeeSettingsForm.get('OnlineRentalCCFeeType');
    const feeControl = this.creditCardFeeSettingsForm.get('OnlineRentalCCFee');

    const feeType = feeTypeControl?.value;

    if (feeType !== 'Fixed' && feeType !== 'Percentage') {
      return;
    }

    const value = feeControl?.value;

    if (value === null || value === undefined || value === '') {
      feeControl?.setValue(0, { emitEvent: false });
    }

    // Allow Angular to render the correct formatter directive first.
    setTimeout(() => {
      const inputId = feeType === 'Fixed' ? '#ccProcessingFeeFixed' : '#ccProcessingFeePercentage';
      const input = document.querySelector<HTMLInputElement>(inputId);
      if (!input) return;
      const currentValue = Number(feeControl?.value ?? 0);
      if (feeType === 'Fixed') {
        input.value = `$${currentValue.toFixed(2)}`;
      } else {
        input.value = `${currentValue.toFixed(2)}%`;
      }
    });
  }

  private updateExcludeProcessingFeeControls(enabled: boolean): void {
    const rentalControl = this.creditCardFeeSettingsForm.get('ExcludeCCProcessingFeeForRental');
    const posControl = this.creditCardFeeSettingsForm.get('ExcludeCCProcessingFeeForPOS');

    if (enabled) {
      rentalControl?.setValue(false, { emitEvent: false });
      posControl?.setValue(false, { emitEvent: false });
      rentalControl?.disable({ emitEvent: false });
      posControl?.disable({ emitEvent: false });
    } else {
      rentalControl?.enable({ emitEvent: false });
      posControl?.enable({ emitEvent: false });
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public created(args1) {
    this.gridCreditDebit.element.querySelectorAll('.e-filterdiv')[this.gridCreditDebit.getColumnIndexByField('ChargeType')].classList.add('e-hide');
    this.gridCreditDebit.element.querySelectorAll('.e-filterdiv')[this.gridCreditDebit.getColumnIndexByField('Charge')].classList.add('e-hide');
    this.gridCreditDebit.element.querySelectorAll('.e-filterdiv')[this.gridCreditDebit.getColumnIndexByField('IsInactive')].classList.add('e-hide');
    this.gridCreditDebit.element.querySelectorAll('.e-filterdiv')[this.gridCreditDebit.getColumnIndexByField('Delete')].classList.add('e-hide');
    this.gridCreditDebit.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
